/**
 * Footer — link columns, social, copyright.  All driven by config.footer.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { COLOR, FONT } from '../../../components/ss/tokens';
import useSiteConfig from '../useSiteConfig';

function isExternal(href) {
  return /^(https?:|mailto:|tel:)/i.test(href || '');
}

function FooterLink({ link }) {
  if (!link?.href) return null;
  const style = {
    color: COLOR.textMuted,
    textDecoration: 'none',
    fontSize: FONT.size.base,
    lineHeight: 2,
  };
  if (isExternal(link.href) || link.href.includes('#')) {
    return <a href={link.href} style={style}>{link.label}</a>;
  }
  return <Link to={link.href} style={style}>{link.label}</Link>;
}

export default function Footer() {
  const { config } = useSiteConfig();
  const { brand, footer } = config;
  const fb = config.ui?.fallbacks || {};
  const copyright = footer?.copyright
    || `${fb.footerCopyright || '©'} ${brand.name}`;

  return (
    <>
      {/* Qatar-style decorative wave crest rising into the footer */}
      <div aria-hidden style={{ background: '#f7f3f4', lineHeight: 0 }}>
        <svg viewBox="0 0 1440 90" preserveAspectRatio="none"
             style={{ display: 'block', width: '100%', height: 70 }}>
          <path d="M0,40 C 240,90 480,90 720,55 C 960,20 1200,20 1440,55 L1440,90 L0,90 Z"
                fill="#4a1228" opacity="0.35" />
          <path d="M0,55 C 240,95 480,95 720,65 C 960,35 1200,35 1440,62 L1440,90 L0,90 Z"
                fill="#4a1228" opacity="0.6" />
          <path d="M0,68 C 240,96 480,96 720,76 C 960,56 1200,56 1440,72 L1440,90 L0,90 Z"
                fill="#4a1228" />
        </svg>
      </div>
    <footer
      className="hhms-marketing-footer"
      style={{
        background: '#4a1228',
        color: '#e8d6cd',
        padding: '24px 56px 28px',
      }}
    >
      <div
        style={{
          maxWidth: '100%',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 1.4fr) repeat(3, minmax(160px, 1fr))',
          gap: 40,
        }}
        className="hhms-marketing-footer-grid"
      >
        {/* Brand column */}
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 14,
            }}
          >
            <span style={{ fontSize: 22 }}>{brand.logoEmoji || '⚕️'}</span>
            <span
              style={{
                fontSize: 18,
                fontWeight: FONT.weight.xbold,
                color: '#fff',
              }}
            >
              {brand.name}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: FONT.size.base, lineHeight: 1.6, color: '#90a4ae' }}>
            {footer?.tagline || brand.tagline}
          </p>
          {footer?.social?.length > 0 && (
            <div style={{ display: 'flex', gap: 14, marginTop: 18 }}>
              {footer.social.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#c4d4e3',
                    textDecoration: 'none',
                    fontSize: FONT.size.base,
                    border: '1px solid rgba(196,212,227,0.2)',
                    borderRadius: 999,
                    padding: '4px 12px',
                  }}
                >
                  {s.label}
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Link columns */}
        {(footer?.columns || []).map((col) => (
          <div key={col.title}>
            <div
              style={{
                fontSize: FONT.size.sm,
                fontWeight: FONT.weight.bold,
                color: '#fff',
                textTransform: 'uppercase',
                letterSpacing: '1.2px',
                marginBottom: 14,
              }}
            >
              {col.title}
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {(col.links || []).map((l) => (
                <li key={l.label}>
                  <FooterLink link={l} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div
        style={{
          maxWidth: '100%',
          margin: '0 auto',
          marginTop: 48,
          paddingTop: 22,
          borderTop: '1px solid rgba(196,212,227,0.12)',
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
          fontSize: FONT.size.sm,
          color: '#90a4ae',
        }}
      >
        <span>{copyright}</span>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <a href="/privacy" style={{ color: '#90a4ae', textDecoration: 'none', fontSize: 13 }}>Privacy Policy</a>
          <a href="/terms"   style={{ color: '#90a4ae', textDecoration: 'none', fontSize: 13 }}>Terms of Service</a>
          <span>{brand.fullName}</span>
        </div>
      </div>

      <style>{`
        @media (max-width: 820px) {
          .hhms-marketing-footer-grid {
            grid-template-columns: 1fr 1fr !important;
          }
        }
        @media (max-width: 640px) {
          .hhms-marketing-footer { padding-left: 20px !important; padding-right: 20px !important; }
        }
        @media (max-width: 480px) {
          .hhms-marketing-footer-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </footer>
    </>
  );
}
